import React from 'react';
import { CheckIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { CtaBand } from '../components/CtaBand';
import { Reveal } from '../components/showcase/Reveal';
import { ResponseTimeVignette } from '../components/showcase/ResponseTimeVignette';
import { howWeWorkItems } from '../data/content';
import { usePageTitle } from '../hooks/usePageTitle';
export function About() {
  usePageTitle('About | Viridia Analytics');
  return <>
      <PageHeader eyebrow="About" title="We build response systems for businesses that pay to make the phone ring." body="Viridia Analytics is a Phoenix-based company founded by Kevin Rogers." data-id="element-525" data-mp="377drkagaaxp2" />

      <section aria-labelledby="why-title" className="border-b border-white/5 bg-ink-800" data-id="element-526" data-mp="2x8evdrcpguiy">
        <div className="mx-auto max-w-content px-5 py-20 sm:px-8 sm:py-24" data-id="element-527" data-mp="1gtwvb8o2u6ii">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand" data-id="element-528" data-mp="1lukjlmkx8991">
            Our reason
          </p>
          <h2 id="why-title" className="mt-5 max-w-2xl font-display text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-529" data-mp="1uil3jgxrycix">
            Why Viridia exists.
          </h2>
          <div className="mt-8 max-w-3xl space-y-5 text-base leading-relaxed text-white/70 sm:text-lg" data-id="element-530" data-mp="23jrp44fa6dj1">
            <p data-id="element-531" data-mp="1ylhrln6330u9">
              Viridia Analytics is a Phoenix-based company founded by Kevin Rogers that
              builds response systems for lead-driven businesses.
            </p>
            <p data-id="element-532" data-mp="28h1i1u1vp7bm">
              The story is simple. Lead-driven businesses pay real money to make the phone
              ring and then lose the race to answer it. We started in home services, where
              a missed call costs a job, and built the system that answers every lead in
              seconds and books it.
            </p>
          </div>

          <Reveal className="mt-10 max-w-3xl rounded-xl border border-white/10 bg-ink-700 p-7 sm:p-8" data-id="element-533" data-mp="2r8klg1ztene6">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand" data-id="element-534" data-mp="1ov67nbg4ob05">
              Five years inside the portal
            </p>
            <p className="mt-4 text-base leading-relaxed text-white/75" data-id="element-535" data-mp="1swhu583pgg44">
              Kevin Rogers spent five years at Zillow as a Senior Elite Account Executive.
              He watched great agents lose to faster ones, and he knows exactly how the
              Premier Agent scorecard rewards the teams that answer first. Viridia is the
              system he wished his clients had.
            </p>
          </Reveal>
        </div>
      </section>

      <section aria-labelledby="results-title" className="border-b border-white/5 bg-ink-900" data-id="element-536" data-mp="2glkhmwl0t36j">
        <div className="mx-auto max-w-content px-5 py-20 sm:px-8 sm:py-24" data-id="element-537" data-mp="2ljm1hjq9t6j2">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand" data-id="element-538" data-mp="m8kwknxvfgrv">
            Measured
          </p>
          <h2 id="results-title" className="mt-5 max-w-2xl font-display text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-539" data-mp="r7nqbc8ei0kj">
            The results that sent us here.
          </h2>

          <figure className="mt-12" data-id="element-540" data-mp="eutsahk6yk8y">
            <figcaption className="text-sm font-semibold uppercase tracking-[0.14em] text-white/45" data-id="element-541" data-mp="9wsadzyi4qz2">
              Home services client, same response-time system:
            </figcaption>
            <Reveal className="mt-6" data-id="element-542" data-mp="12mr4i9rjzeal">
              <ResponseTimeVignette data-id="element-543" data-mp="12lpg73lj0vax" />
            </Reveal>
          </figure>

          <p className="mt-10 max-w-3xl font-display text-xl font-semibold leading-snug text-white sm:text-2xl" data-id="element-544" data-mp="xo62c4sqqkji">
            Different industry. Identical disease.
          </p>
        </div>
      </section>

      <section aria-labelledby="how-we-work-title" className="border-b border-white/5 bg-ink-800" data-id="element-545" data-mp="oleqi3vydmrc">
        <div className="mx-auto max-w-content px-5 py-20 sm:px-8 sm:py-24" data-id="element-546" data-mp="3dflj9zgc4rmw">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand" data-id="element-547" data-mp="39bvxtiuyiq63">
            Terms
          </p>
          <h2 id="how-we-work-title" className="mt-5 max-w-2xl font-display text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-548" data-mp="112o4da0louox">
            How we work.
          </h2>

          <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-549" data-mp="r3ylbdwyx473">
            {howWeWorkItems.map((item, index) => <li key={item} className="h-full" data-id="element-550" data-mp="1jtyuhoyi53fr" data-mpi={index}>
                <Reveal delay={0.07 * index} className="h-full" data-id="element-551" data-mp="1jv9tlbh05n0j">
                  <div className="flex h-full items-start gap-3 rounded-xl border border-white/10 bg-ink-700 p-6 text-sm font-medium text-white/80" data-id="element-552" data-mp="1osievkxg6760">
                    <CheckIcon className="mt-0.5 h-4 w-4 flex-none text-brand" aria-hidden="true" data-id="element-553" data-mp="1aw4wm9l3a1qh" />
                    <span className="leading-relaxed" data-id="element-554" data-mp="3vj81kgbfivxs">{item}</span>
                  </div>
                </Reveal>
              </li>)}
          </ul>
        </div>
      </section>

      <CtaBand title="Talk to the people who built the system." body="Fifteen minutes on your lead sources, your response times, and what a fully answered pipeline is worth to your team." data-id="element-555" data-mp="3s12797d5cp8w" />
    </>;
}